import { useMemo } from 'react'
import styled from 'styled-components'
import { Typography } from '@healingpaper/cell'
import { IcDotBubbleOutlined, IcEye, IcThumbsUpOutlined } from '@healingpaper/cell-icons'

import LoginRequiredContentArea from '../../molecules/LoginRequiredContentArea'

import CheetahIcon from '@/components/atomic/atoms/CheetahIcon'
import { CheetahIconPack } from '@/components/atomic/atoms/CheetahIcon/enum'
import PreviewPhotoSlider, { type PreviewPhoto } from '@/components/atomic/organisms/PreviewPhotoSlider'
import UserProfileInCommunity from '@/components/atomic/organisms/UserProfileInCommunity'
import { categoryIconById } from '@/components/atomic/templates/CommunityListTemplate/constants/categories'
import { useTranslation } from '@/lang'
import { getCommunityContentVisibility } from '@/utils/community'
import { formatNumberUpTo9999 } from '@/utils/number'
import { generateTagId } from '@/utils/test-automation'
import { CategoryId, type EngagementType } from '@/features/community/api/document/community-document.types'
import { Languages, PathnamePrefix } from '@/utils/language'

export interface DocumentWriterInfo {
  id: number
  profile: string
  nickname: string
  level: number
  doctorId: number | null
  engagementType: EngagementType | null
}

export interface InfoInUserProfile {
  writer: DocumentWriterInfo
  isAdmin: boolean
  createTime: string
  edited: boolean
  hideUserBadge?: boolean
}

export interface CommunityDocumentListItemProps {
  id: number
  categoryId: CategoryId
  categoryName: string
  writer: DocumentWriterInfo
  writerDoctorId: number | null
  hasThumbUp: boolean
  hasDoctorComment: boolean
  thumbUpCount: number
  commentCount: number
  viewCount: number
  createTime: string
  edited: boolean
  title: string
  contents: string
  photos: PreviewPhoto[]
  lang: Languages
  translateResult: { contents: string } | null
}

const CommunityListItem = (props: {
  item: CommunityDocumentListItemProps
  userId: number | undefined
  language: string
  pathPrefix: PathnamePrefix
  isMedicalTip?: boolean
  onClickContentWrapper: (event: React.MouseEvent<HTMLElement>, item: CommunityDocumentListItemProps) => void
  refineContents: (
    item: {
      categoryId?: CategoryId
      contents: string
      translateResultContents?: string
    },
    showMoreText: string,
    pathPrefix: PathnamePrefix,
  ) => string
  onClickLoginAndWatch?: (item: CommunityDocumentListItemProps) => void
  onClickLikeButton?: () => Promise<void> | void
  onClickDoctorProfile?: () => void
  listIndex?: number
}): JSX.Element => {
  const { t } = useTranslation()

  const contentVisibility = useMemo(() => {
    return getCommunityContentVisibility(props.userId, props.item.writer.doctorId, props.item.categoryId)
  }, [props.userId, props.item.writer.doctorId, props.item.categoryId])

  return (
    <StyledListItem
      key={props.item.id}
      id={generateTagId('community', 'card', 'btn', 'moveTo', `${props.listIndex ?? props.item.id}`)}
      data-testid="community-list-item"
      onClick={(event) => {
        props.onClickContentWrapper(event, props.item)
      }}
    >
      <StyledListItemContent>
        <StyledCategoryTag>
          <CheetahIcon width={16} height={16} name={categoryIconById[props.item.categoryId]} viewBox="0 0 48 48" />
          <StyledCategoryLabel data-testid="category-label">{props.item.categoryName}</StyledCategoryLabel>
        </StyledCategoryTag>
        <UserProfileInCommunity
          type="document"
          profileImageURL={props.item.writer.profile}
          name={props.item.writer.nickname}
          engagementType={props.item.writer.engagementType}
          detail={{
            doctorId: props.item.writer.doctorId ?? undefined,
            createTime: props.item.createTime,
            isEditedTextVisible: props.item.edited,
            level: props.item.writer.level,
          }}
          onClickDoctorProfile={props.onClickDoctorProfile}
        />
        <StyledContentWrapper>
          {props.item.title !== '' && <StyledTitle>{props.item.title}</StyledTitle>}
          {contentVisibility === 'VISIBLE' ? (
            <StyledDocumentContents
              data-testid="content"
              dangerouslySetInnerHTML={{
                __html: props.refineContents(
                  {
                    contents: props.item.contents,
                    categoryId: props.item.categoryId,
                    translateResultContents: props.item.translateResult?.contents || props.item.contents,
                  },
                  t('treatment.seeMore'),
                  props.pathPrefix,
                ),
              }}
            ></StyledDocumentContents>
          ) : (
            <LoginRequiredContentArea
              text={{
                title:
                  contentVisibility === 'HIDDEN_DOCTOR_CONTENT'
                    ? t('pages.communityDetail.curiousAboutContentOfDoctor')
                    : t('pages.communityDetail.curiousAboutReviews'),
                buttonLabel: t('pages.communityDetail.loginAndWatch'),
                description:
                  contentVisibility === 'HIDDEN_DOCTOR_CONTENT'
                    ? t('pages.communityDetail.shouldLogInToWatch')
                    : t('pages.communityDetail.loginIsNeededToWatchReviews'),
              }}
              blurImageSrc="/images/community/list-doctor-blur.png"
              onClickButton={() => props.onClickLoginAndWatch?.(props.item)}
            >
              {contentVisibility === 'HIDDEN_DOCTOR_CONTENT' ? (
                <CheetahIcon width={26} height={26} name={CheetahIconPack.DOCTOR} />
              ) : (
                <img width={32} src="/images/icon/shoe-icon.svg" alt="shoe icon" />
              )}
            </LoginRequiredContentArea>
          )}
        </StyledContentWrapper>

        <ContentTranslatedFrom writtenLanguage={props.item.lang} browserLanguage={props.language} />

        {contentVisibility === 'VISIBLE' && !!props.item.photos?.length && (
          <StyledPreviewPhotoSlider
            aria-label="photo slider"
            photos={props.item.photos}
            width="120px"
            height="120px"
            isLocked={props.isMedicalTip && !props.userId}
          />
        )}

        <StyledCreateTime>{props.item.createTime}</StyledCreateTime>

        {props.item.hasDoctorComment && (
          <StyledHasDoctorComment>
            <StyledDoctorIcon name={CheetahIconPack.DOCTOR} />
            <span>
              {t('pages.communityList.doctor')} {t('pages.communityList.comment')}
            </span>
          </StyledHasDoctorComment>
        )}
      </StyledListItemContent>

      {!props.isMedicalTip && (
        <>
          <StyledDivider />
          <StyledCommunityActionBar data-testid="reaction-bar">
            <StyledIconTextButton
              data-testid="like-button"
              onClick={(e) => {
                e.stopPropagation()
                props.onClickLikeButton?.()
              }}
            >
              {props.item.hasThumbUp ? (
                <IcThumbsUpOutlined width={16} height={16} data-testid="thumbs-up-on-icon" />
              ) : (
                <IcThumbsUpOutlined width={16} height={16} data-testid="thumbs-up-off-icon" />
              )}
              <div>
                {props.item.thumbUpCount > 0
                  ? formatNumberUpTo9999(props.item.thumbUpCount)
                  : t('pages.communityList.like')}
              </div>
            </StyledIconTextButton>
            <StyledIconTextButton data-testid="reply-button" $notClickable>
              <StyledReplyLink data-testid="reply-link">
                <IcDotBubbleOutlined width={16} height={16} />
                <div>
                  {props.item.categoryId === CategoryId.PLASTIC_SURGERY_QUESTION
                    ? t('pages.communityList.reply')
                    : t('pages.communityList.comment')}{' '}
                  {props.item.commentCount > 0
                    ? formatNumberUpTo9999(props.item.commentCount)
                    : t('pages.communityList.write')}
                </div>
              </StyledReplyLink>
            </StyledIconTextButton>
            <StyledIconTextButton data-testid="view-button" $notClickable>
              <IcEye width={16} height={16} />
              <div>
                {t('pages.communityList.view')} {formatNumberUpTo9999(props.item.viewCount)}
              </div>
            </StyledIconTextButton>
          </StyledCommunityActionBar>
        </>
      )}
    </StyledListItem>
  )
}

interface ContentTranslatedFromProps {
  writtenLanguage: 'ko' | 'ja' | string | null
  browserLanguage: string
}

const ContentTranslatedFrom = (props: ContentTranslatedFromProps) => {
  const { t } = useTranslation()

  if (!props.writtenLanguage) return null
  const isSameLanguage = props.browserLanguage.includes(props.writtenLanguage)
  if (isSameLanguage) return null

  let writtenLanguageText = ''
  if (props.writtenLanguage === 'ko') writtenLanguageText = t(`webCommunityTranslationKorean`)
  if (props.writtenLanguage === 'ja') writtenLanguageText = t(`webCommunityTranslationJapanese`)
  if (props.writtenLanguage === 'en') writtenLanguageText = t(`webCommunityTranslationEnglish`)

  return (
    <StyledContentTranslatedFrom>
      <Typography type="description2" color="gray700">
        {t('webCommunityTranslationDescription', { language: writtenLanguageText })}
      </Typography>
    </StyledContentTranslatedFrom>
  )
}

const StyledContentTranslatedFrom = styled.div`
  margin-top: 12px;
`

const StyledListItem = styled.div``

const StyledListItemContent = styled.div`
  padding: 26px;
`

const StyledCategoryTag = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 2px;
  margin-bottom: 16px;
  height: 16px;
  padding: 3px 6px 3px 5px;
  border: 1px solid ${({ theme }) => theme.colors.gray300};
  border-radius: 4px;
`

const StyledCategoryLabel = styled.span`
  ${({ theme }) => theme.typography.caption2}
  color: ${({ theme }) => theme.colors.gray800};
`

const StyledContentWrapper = styled.div`
  ${({ theme }) => theme.typography.description1}
  color: ${({ theme }) => theme.colors.black};
`

const StyledTitle = styled.h2`
  margin: 12px 0px 8px;
  ${({ theme }) => theme.typography.title3}
`

const StyledDocumentContents = styled.span`
  display: block;
  margin-top: 12px;
  white-space: pre-line;
  .show-more-text {
    color: ${({ theme }) => theme.colors.gray700};
    text-decoration: none;
    cursor: pointer;
  }
  .link-style {
    color: ${({ theme }) => theme.colors.gray800};
    text-decoration: underline;
  }
  .hash-tag-button {
    ${({ theme }) => theme.typography.description1};
    color: ${({ theme }) => theme.colors.latte};
    border: unset;
    background-color: initial;
    text-decoration: unset;
  }
`

const StyledPreviewPhotoSlider = styled(PreviewPhotoSlider)`
  margin-top: 16px;
  margin-left: -26px;
  margin-right: -26px;
  padding-left: 10px;
  padding-right: 10px;
`

const StyledCreateTime = styled.div`
  ${({ theme }) => theme.typography.caption1};
  color: ${({ theme }) => theme.colors.gray700};
  margin-top: 8px;
`

const StyledHasDoctorComment = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 2px;
  align-items: center;
  margin-top: 16px;
  ${({ theme }) => theme.typography.description2};
  color: ${({ theme }) => theme.colors.gray800};
`

const StyledDoctorIcon = styled(CheetahIcon)`
  position: relative;
  top: 1px;
`

const StyledDivider = styled.div`
  height: 1px;
  background-color: ${({ theme }) => theme.colors.gray300} !important;
`

const StyledCommunityActionBar = styled.section`
  display: flex;
  height: 52px;
  padding: 0 16px;
  justify-content: center;

  & > button {
    flex: 1;
  }
`

const StyledIconTextButton = styled.button<{ $notClickable?: boolean }>`
  display: flex;
  justify-content: center;
  align-items: center;
  ${({ theme }) => theme.typography.description2};
  color: ${({ theme }) => theme.colors.gray800};
  background-color: unset;
  border: unset;
  gap: 4px;
  padding: unset;
  cursor: ${(props) => (props.$notClickable ? 'default' : 'pointer')};
`

const StyledReplyLink = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  color: ${({ theme }) => theme.colors.gray800};
  text-decoration: unset;
`

export default CommunityListItem
